import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Droplet } from "lucide-react";
import { useUnits } from "../context/UnitContext";
import { navLinks } from "../lib/content";

const UnitToggle = () => {
    const { units, toggle } = useUnits();
    return (
        <button
            type="button"
            onClick={toggle}
            data-testid="unit-toggle"
            className="inline-flex items-center border border-foreground bg-background hover:bg-foreground hover:text-background transition-colors text-xs font-mono uppercase tracking-widest"
            aria-label="Toggle measurement units"
        >
            <span
                className={`px-3 py-2 ${units === "metric" ? "bg-foreground text-background" : ""}`}
                data-testid="unit-metric-label"
            >
                Metric
            </span>
            <span
                className={`px-3 py-2 ${units === "imperial" ? "bg-foreground text-background" : ""}`}
                data-testid="unit-imperial-label"
            >
                Imperial
            </span>
        </button>
    );
};

const Header = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <header className="sticky top-0 z-40 bg-background border-b border-foreground" data-testid="site-header">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between h-16">
                <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
                    <span className="inline-flex h-8 w-8 items-center justify-center bg-primary text-white">
                        <Droplet className="h-4 w-4" strokeWidth={2.5} />
                    </span>
                    <span className="font-black text-lg tracking-tighter uppercase">
                        Hydration<span className="text-primary">.</span>Hub
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-5 lg:gap-7" data-testid="primary-nav">
                    {navLinks.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-sm font-bold uppercase tracking-widest transition-colors ${
                                    isActive ? "text-primary underline underline-offset-8 decoration-2" : "hover:text-primary"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <div className="hidden sm:block">
                        <UnitToggle />
                    </div>
                    <button
                        type="button"
                        onClick={() => setOpen(!open)}
                        className="md:hidden p-2 border border-foreground"
                        data-testid="mobile-menu-toggle"
                        aria-label="Open menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="md:hidden border-t border-foreground bg-background" data-testid="mobile-nav">
                    <div className="px-4 py-6 space-y-4">
                        {navLinks.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `block text-2xl font-black uppercase tracking-tight ${isActive ? "text-primary" : "text-foreground"}`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <div className="pt-2">
                            <UnitToggle />
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

const Footer = () => (
    <footer className="border-t border-foreground bg-foreground text-background mt-24" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
                <div className="font-black text-2xl tracking-tighter uppercase mb-3">
                    Hydration<span className="text-primary">.</span>Hub
                </div>
                <p className="text-background/70 max-w-md text-sm leading-relaxed">
                    A free, science-backed hydration hub. Calculators, daily targets, and
                    research-grounded guides — no accounts, no paywalls, no sign-ups.
                </p>
            </div>
            <div>
                <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-background/50 mb-4">
                    Tools
                </h4>
                <ul className="space-y-2 text-sm">
                    <li><Link className="hover:text-primary" to="/">Daily Water Calculator</Link></li>
                    <li><Link className="hover:text-primary" to="/calculators">All Calculators</Link></li>
                    <li><Link className="hover:text-primary" to="/calculators#converter">Unit Converter</Link></li>
                </ul>
            </div>
            <div>
                <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-background/50 mb-4">
                    Company
                </h4>
                <ul className="space-y-2 text-sm">
                    <li><Link className="hover:text-primary" to="/about">About</Link></li>
                    <li><Link className="hover:text-primary" to="/blog">Blog</Link></li>
                    <li><Link className="hover:text-primary" to="/contact">Contact</Link></li>
                    <li><Link className="hover:text-primary" to="/privacy-policy">Privacy Policy</Link></li>
                    <li><a className="hover:text-primary" href="/sitemap.xml" target="_blank" rel="noopener noreferrer" data-testid="footer-sitemap-link">Sitemap</a></li>
                </ul>
            </div>
        </div>
        <div className="border-t border-background/20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs font-mono uppercase tracking-widest text-background/50">
                <span>&copy; {new Date().getFullYear()} Hydration.Hub</span>
                <span>Not medical advice. Consult a physician for medical concerns.</span>
            </div>
        </div>
    </footer>
);

export const Layout = ({ children }) => (
    <div className="App min-h-screen flex flex-col">
        <Header />
        <main className="flex-1">{children}</main>
        <Footer />
    </div>
);
